import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  Check,
  Zap,
  Crown,
  Rocket,
  Copy,
  Loader2,
  ExternalLink,
  AlertTriangle,
  Gift,
  Sparkles,
  TrendingUp,
  Lock,
  MessageCircle,
  Wallet,
  AlertCircle
} from 'lucide-react';

function PricingCard({ tier, name, price, yearly, features, popular, currentTier, onSelect, highlight }) {
  const isCurrentPlan = currentTier === tier;
  
  const tierStyles = {
    free: { bg: 'bg-dark-700', icon: Copy, iconColor: 'text-dark-400' },
    basic: { bg: 'bg-emerald-500/20', icon: Zap, iconColor: 'text-emerald-400' },
    pro: { bg: 'bg-primary-500/20', icon: Rocket, iconColor: 'text-primary-400' },
    whale: { bg: 'bg-gradient-to-br from-amber-500/30 to-orange-500/20', icon: Crown, iconColor: 'text-amber-400' }
  };
  
  const style = tierStyles[tier] || tierStyles.free;
  const Icon = style.icon;
  
  return (
    <div className={`card relative flex flex-col ${
      popular ? 'border-primary-500 ring-2 ring-primary-500/20' : 
      highlight ? 'border-amber-500/50 ring-1 ring-amber-500/20' : ''
    }`}>
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-primary-500 to-accent-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
            MOST POPULAR
          </span>
        </div>
      )}
      
      <div className="text-center mb-6">
        <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center ${style.bg} shadow-lg`}>
          <Icon className={`w-7 h-7 ${style.iconColor}`} />
        </div>
        <h3 className="text-xl font-bold">{name}</h3>
        <div className="mt-3">
          {price === 0 ? (
            <span className="text-4xl font-bold bg-gradient-to-r from-white to-dark-300 bg-clip-text text-transparent">Free</span>
          ) : (
            <>
              <span className="text-4xl font-bold bg-gradient-to-r from-white to-dark-300 bg-clip-text text-transparent">${price}</span>
              <span className="text-dark-400 text-lg">/month</span>
            </>
          )}
        </div>
        {yearly && (
          <p className="text-sm text-dark-500 mt-2">
            or <span className="text-dark-300 font-medium">${yearly}/year</span> (save {Math.round((1 - yearly / (price * 12)) * 100)}%)
          </p>
        )}
      </div>
      
      <div className="flex-1">
        <ul className="space-y-3 mb-6">
          {features.map((feature, i) => {
            const isNegative = feature.startsWith('❌');
            return (
              <li key={i} className="flex items-start gap-3 text-sm">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  isNegative ? 'bg-red-500/20' : 'bg-emerald-500/20'
                }`}>
                  {isNegative ? (
                    <span className="text-red-400 text-xs">✕</span>
                  ) : (
                    <Check className="w-3 h-3 text-emerald-400" />
                  )}
                </div>
                <span className={isNegative ? 'text-dark-500' : 'text-dark-200'}>{feature}</span>
              </li>
            );
          })}
        </ul>
      </div>
      
      <button
        onClick={() => onSelect(tier)}
        disabled={isCurrentPlan}
        className={`btn w-full ${
          isCurrentPlan ? 'btn-secondary cursor-default opacity-60' :
          tier === 'whale' ? 'btn-gradient' :
          popular ? 'btn-primary' : 'btn-secondary hover:bg-dark-600'
        }`}
      >
        {isCurrentPlan ? '✓ Current Plan' : price === 0 ? 'Get Started' : 'Upgrade Now'}
      </button>
    </div>
  );
}

function ComingSoonCard() {
  return (
    <div className="card relative flex flex-col border-cyan-500/30 bg-gradient-to-br from-cyan-900/10 to-dark-800">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          COMING SOON
        </span>
      </div>
      
      <div className="text-center mb-6 pt-2">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-gradient-to-br from-cyan-500/30 to-blue-500/20 shadow-lg">
          <TrendingUp className="w-7 h-7 text-cyan-400" />
        </div>
        <h3 className="text-xl font-bold text-cyan-300">ARB Mode</h3>
        <p className="text-sm text-dark-400 mt-2">Advanced Arbitrage Engine</p>
      </div>
      
      <div className="flex-1">
        <div className="p-4 rounded-xl bg-dark-700/50 border border-dark-600 mb-6">
          <p className="text-sm text-dark-300 text-center">
            Automated arbitrage detection and execution across prediction markets.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <span className="px-2 py-1 rounded-full text-xs bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">Auto-detection</span>
            <span className="px-2 py-1 rounded-full text-xs bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">Multi-market</span>
            <span className="px-2 py-1 rounded-full text-xs bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">Risk-free profits</span>
          </div>
        </div>
      </div>
      
      <button
        disabled
        className="btn w-full bg-dark-700 text-dark-400 cursor-not-allowed border border-dark-600"
      >
        <Lock className="w-4 h-4 mr-2" />
        Not Available Yet
      </button>
    </div>
  );
}

function PaymentModal({ isOpen, onClose, tier, pricing }) {
  const [duration, setDuration] = useState(1);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const tierPricing = pricing?.tiers?.[tier];
  const amount = duration >= 12 ? tierPricing?.yearly : tierPricing?.monthly * duration;
  
  const createPayment = async () => {
    try {
      setError('');
      const response = await api.post('/payments/create-payment', { tier, duration });
      setPaymentIntent(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create payment');
    }
  };
  
  const verifyPayment = async () => {
    if (!txHash.trim()) {
      setError('Please enter the transaction hash');
      return;
    }
    
    setIsVerifying(true);
    setError('');
    
    try {
      const response = await api.post('/payments/verify-transaction', {
        txHash: txHash.trim(),
        tier,
        duration
      });
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify payment');
    } finally {
      setIsVerifying(false);
    }
  };
  
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-xl max-w-md w-full p-6">
        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">Subscription Activated!</h3>
            <p className="text-dark-400">Your {tierPricing?.name} plan is now active.</p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold mb-2">Upgrade to {tierPricing?.name}</h2>
            
            {/* CRITICAL: Polygon USDC Warning */}
            <div className="p-3 bg-amber-500/10 border-2 border-amber-500/50 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                <span className="font-bold text-amber-400">⚠️ POLYGON USDC ONLY</span>
              </div>
              <p className="text-sm text-amber-200">
                Only send <span className="font-bold">USDC on the Polygon network</span>. 
                Sending from other networks (Ethereum, Arbitrum, etc.) will result in <span className="font-bold text-red-400">permanent loss of funds</span>.
              </p>
            </div>
            
            {/* Connected Wallet Notice */}
            <div className="p-3 bg-primary-500/10 border border-primary-500/30 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-primary-400" />
                <p className="text-sm text-primary-300">
                  <span className="font-bold">Send from your connected wallet</span> for instant activation.
                </p>
              </div>
            </div>
            
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm mb-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            {/* Duration selector */}
            <div className="mb-4">
              <label className="label">Duration</label>
              <select
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                className="input"
              >
                <option value={1}>1 Month - ${tierPricing?.monthly}</option>
                <option value={3}>3 Months - ${tierPricing?.monthly * 3}</option>
                <option value={6}>6 Months - ${tierPricing?.monthly * 6}</option>
                <option value={12}>12 Months - ${tierPricing?.yearly} (Save {Math.round((1 - tierPricing?.yearly / (tierPricing?.monthly * 12)) * 100)}%)</option>
              </select>
            </div>
            
            {/* Payment details */}
            <div className="p-4 bg-dark-700/50 rounded-lg mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-dark-400">Amount</span>
                <span className="font-bold">{amount} USDC</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-dark-400">Network</span>
                <span className="text-primary-400">Polygon</span>
              </div>
              {paymentIntent && (
                <div className="mt-3 pt-3 border-t border-dark-600">
                  <p className="text-sm text-dark-400 mb-2">Send to:</p>
                  <code className="block p-2 bg-dark-800 rounded text-xs break-all">
                    {paymentIntent.paymentAddress}
                  </code>
                </div>
              )}
            </div>
            
            {!paymentIntent ? (
              <button onClick={createPayment} className="btn btn-primary w-full mb-3">
                Generate Payment Address
              </button>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="label">Transaction Hash</label>
                  <input
                    type="text"
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    className="input font-mono text-sm"
                    placeholder="0x..."
                  />
                  <p className="text-xs text-dark-500 mt-1">
                    Paste the transaction hash after sending USDC
                  </p>
                </div>
                
                <button
                  onClick={verifyPayment}
                  disabled={isVerifying}
                  className="btn btn-primary w-full"
                >
                  {isVerifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    'Verify Payment'
                  )}
                </button>
              </div>
            )}
            
            
            {/* Discord Support */}
            <div className="mt-4 p-3 bg-dark-700/50 border border-dark-600 rounded-lg">
              <div className="flex items-center gap-2 justify-center">
                <MessageCircle className="w-4 h-4 text-indigo-400" />
                <span className="text-sm text-dark-300">Having issues?</span>
                <a
                  href="https://discord.gg/dFSefc9Hs9"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-indigo-400 hover:text-indigo-300 underline"
                >
                  Join our Discord
                </a>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="w-full text-center text-dark-400 text-sm mt-4 hover:text-white"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function PromoCodeSection({ onSuccess }) {
  const [code, setCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const redeemCode = async () => {
    if (!code.trim()) {
      setError('Please enter a promo code');
      return;
    }

    setIsRedeeming(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/promo/redeem', { code: code.trim().toUpperCase() });
      setSuccess(`🎉 ${response.data.message}`);
      setCode('');
      if (onSuccess) {
        setTimeout(onSuccess, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to redeem code');
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <div id="promo" className="max-w-md mx-auto">
      <div className="card card-glow bg-gradient-to-br from-primary-900/20 to-accent-900/20 border-primary-500/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold">Have a Promo Code?</h3>
            <p className="text-sm text-dark-400">Enter it below for instant access</p>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            {success}
          </div>
        )}

        <div className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ENTER CODE"
            className="input flex-1 font-mono tracking-wider uppercase"
            onKeyDown={(e) => e.key === 'Enter' && redeemCode()}
          />
          <button
            onClick={redeemCode}
            disabled={isRedeeming}
            className="btn btn-gradient px-6"
          >
            {isRedeeming ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Redeem'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [pricingRes, subRes] = await Promise.all([
        api.get('/payments/pricing'),
        api.get('/payments/subscription')
      ]);
      setPricing(pricingRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSelectTier = (tier) => {
    if (tier === 'free') {
      navigate('/');
    } else {
      setSelectedTier(tier);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-3">Choose Your Plan</h1>
        <p className="text-dark-400">
          Unlock advanced features and maximize your copy trading potential.
          Pay with USDC on Polygon - no credit card needed.
        </p>
      </div>
      
      {/* Promo Code Section */}
      <PromoCodeSection onSuccess={fetchData} />
      
      {/* Current subscription */}
      {subscription && subscription.tier !== 'free' && (
        <div className="max-w-md mx-auto p-4 bg-primary-500/10 border border-primary-500/20 rounded-lg text-center">
          <p className="text-sm text-primary-400">
            Current plan: <span className="font-bold capitalize">{subscription.tier}</span>
            {subscription.endDate && (
              <span className="text-dark-400"> • Expires {new Date(subscription.endDate).toLocaleDateString()}</span>
            )}
          </p>
        </div>
      )}
      
      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
        <PricingCard
          tier="free"
          name="Free"
          price={0}
          features={[
            '1 copy configuration',
            '3 max positions',
            '10 trades/month',
            'Basic market data',
            'Community support',
            '❌ No Find Traders'
          ]}
          currentTier={subscription?.tier}
          onSelect={handleSelectTier}
        />
        
        <PricingCard
          tier="basic"
          name="Basic"
          price={29}
          yearly={290}
          features={[
            '5 copy configurations',
            '10 max positions',
            '500 trades/month',
            '$100k monthly volume',
            'Paper trading (2 positions)',
            '🔍 Find Traders',
            'Manual trading',
            'Custom alerts',
            'Email support'
          ]}
          currentTier={subscription?.tier}
          onSelect={handleSelectTier}
        />
        
        <PricingCard
          tier="pro"
          name="Pro"
          price={99}
          yearly={990}
          popular={true}
          features={[
            '10 copy configurations',
            '50 max positions',
            '2,000 trades/month',
            '$500k monthly volume',
            'Paper trading (5 positions)',
            '🔍 Find Traders',
            'Advanced analytics',
            'Priority execution',
            'Trader signals',
            'API access',
            'Priority support'
          ]}
          currentTier={subscription?.tier}
          onSelect={handleSelectTier}
        />
        
        <PricingCard
          tier="whale"
          name="Whale"
          price={499}
          yearly={4990}
          highlight={true}
          features={[
            'Unlimited copy configs',
            'Unlimited positions',
            'Unlimited trades',
            'Paper trading (15 positions)',
            '🔍 Find Traders',
            'All Pro features',
            '🐋 Whale Vault access',
            'Crypto price prediction strategies',
            'Backtesting tools',
            'Custom integrations',
            'Dedicated support',
            'Early access to new features'
          ]}
          currentTier={subscription?.tier}
          onSelect={handleSelectTier}
        />
      </div>
      
      {/* ARB Mode Coming Soon */}
      <div className="max-w-sm mx-auto mt-8">
        <ComingSoonCard />
      </div>
      
      {/* FAQ */}
      <div className="max-w-2xl mx-auto mt-12">
        <h2 className="text-xl font-bold mb-4 text-center">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div className="card border-amber-500/30">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              How do I pay?
            </h3>
            <p className="text-sm text-dark-400">
              <span className="font-bold text-amber-400">POLYGON USDC ONLY!</span> Send the exact amount from your connected wallet to the provided address. 
              Paste the transaction hash to verify. <span className="text-red-400">Do NOT send from other networks - funds will be lost.</span>
            </p>
          </div>
          <div className="card">
            <h3 className="font-medium mb-2">Can I upgrade or downgrade?</h3>
            <p className="text-sm text-dark-400">
              Yes! You can upgrade at any time. Downgrades take effect at the end of your billing period.
            </p>
          </div>
          <div className="card">
            <h3 className="font-medium mb-2">Is there a refund policy?</h3>
            <p className="text-sm text-dark-400">
              Due to the nature of crypto payments, we don't offer refunds. Try the free tier first to make sure it's right for you.
            </p>
          </div>
        </div>
        
        {/* Discord Support CTA */}
        <div className="card bg-gradient-to-r from-indigo-900/20 to-purple-900/20 border-indigo-500/30 mt-6">
          <div className="flex items-center justify-center gap-3">
            <MessageCircle className="w-5 h-5 text-indigo-400" />
            <p className="text-sm text-dark-300">
              Questions or payment issues? 
              <a
                href="https://discord.gg/dFSefc9Hs9"
                target="_blank"
                rel="noreferrer"
                className="ml-1 font-medium text-indigo-400 hover:text-indigo-300 underline"
              >
                Join our Discord for support
              </a>
            </p>
          </div>
        </div>
      </div>
      
      {/* Payment Modal */}
      <PaymentModal
        isOpen={!!selectedTier}
        onClose={() => setSelectedTier(null)}
        tier={selectedTier}
        pricing={pricing}
      />
    </div>
  );
}
